/**
 * Pure derivations for the timeline track under the hero (the "five-minute
 * tenant track" and the landlord "application to LMR" track).
 *
 * The clock counts from 0 to the total in seconds; each segment fills while
 * the clock is inside its span, and the landlord "You Approve" pin appears
 * once the clock passes its second. Everything here is unit tested; the
 * component is thin rendering over these functions.
 */

export interface TrackSpanInput {
	/** How long this step takes, in seconds. */
	seconds: number
}

export interface TrackSpan {
	start: number
	end: number
}

/**
 * The track copy as the component reads it. `siteContent` is `as const`, so
 * its arrays are readonly tuples and the tenant side has no `pin`; this is
 * the common, readonly shape both sides satisfy.
 */
export interface TrackView {
	heading: readonly string[]
	unit: string
	segments: readonly {
		seconds: number
		time: string
		name: string
		nameMobile?: readonly [string, string]
	}[]
	pin?: { label: string; atSeconds: number }
}

/** The [start, end] second of every segment, laid end to end from 0. */
export function trackSpans(segments: readonly TrackSpanInput[]): TrackSpan[] {
	let at = 0
	return segments.map((segment) => {
		const start = at
		at += Math.max(0, segment.seconds)
		return { start, end: at }
	})
}

/**
 * A segment's label written out, the way both bars show it: "50 sec",
 * "1 min", "1 min 10 sec", "5 min". The content keeps its own `time`
 * strings; the tests hold them to this.
 */
export function durationLabel(seconds: number) {
	const minutes = Math.floor(seconds / 60)
	const rest = seconds % 60
	if (minutes === 0) return `${rest} sec`
	return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} sec`
}

/** The whole track in seconds: the sum of the segments. */
export function trackTotal(segments: readonly TrackSpanInput[]) {
	return segments.reduce(
		(sum, segment) => sum + Math.max(0, segment.seconds),
		0,
	)
}

/**
 * How full each segment is (0..1) when the clock reads `seconds`. A segment
 * is empty before its span, fills linearly inside it and is full after it,
 * so the bar always reads left to right in step with the clock.
 */
export function segmentProgress(
	seconds: number,
	segments: readonly TrackSpanInput[],
): number[] {
	return trackSpans(segments).map(({ start, end }) => {
		if (end <= start) return seconds >= end ? 1 : 0
		return Math.min(1, Math.max(0, (seconds - start) / (end - start)))
	})
}

/** True once a segment is full and its time label should show. */
export function segmentDone(progress: number) {
	return progress >= 1
}

/** The pin ("You Approve") shows once the clock reaches its second. */
export function pinVisible(seconds: number, atSeconds: number) {
	return seconds >= atSeconds
}

/**
 * Where the pin sits over the bar, as a CSS `left`. The bar is `gapPx`
 * gaps between flex segments, so a plain percentage of the full width drifts
 * off the segment edges by a few pixels; this measures the proportional
 * part across the segments only and then adds the gaps that come before.
 * When the pin sits on a boundary the 2px tick lands centred in that gap.
 */
export function pinLeft(
	segments: readonly TrackSpanInput[],
	atSeconds: number,
	gapPx = 4,
): string {
	const total = trackTotal(segments)
	const spans = trackSpans(segments)
	const gaps = Math.max(0, segments.length - 1)
	const fraction = total > 0 ? Math.min(1, Math.max(0, atSeconds / total)) : 0
	const ended = spans.filter((span) => span.end <= atSeconds).length
	const gapsBefore = Math.min(gaps, ended)
	const onBoundary =
		ended > 0 && ended <= gaps && spans[ended - 1]?.end === atSeconds
	const offsetPx = gapsBefore * gapPx - (onBoundary ? gapPx / 2 : 0)
	const share = Number(fraction.toFixed(4))
	return `calc((100% - ${gaps * gapPx}px) * ${share} + ${offsetPx}px)`
}
